import { type ScoreResultDraftType, type ScoreSetConfigurationFormikType, ScoreType } from "@/modules/scores/scoreTypes"
import { createExerciseScoreFormModel } from "@/modules/sessions/sessionForm/blocks/exerciseBlock/models/exerciseScoreFormModel"
import { useEffect, useState } from "react"

export function useExerciseScoreFormActions(
  initialScoreSetConfigurations: ScoreSetConfigurationFormikType[] | null,
  initialScoringOption: ScoreResultDraftType | null,
) {
  const [scoreSetConfigurations, setScoreSetConfigurations] = useState<ScoreSetConfigurationFormikType[]>(
    initialScoreSetConfigurations && initialScoreSetConfigurations.length > 0
      ? initialScoreSetConfigurations
      : [{ id: null, displayOrder: 0, scoreType: ScoreType.TIME }],
  )
  const [scoringOption, setScoringOption] = useState<ScoreResultDraftType | null>(initialScoringOption ?? null)
  const [scoreType, setScoreType] = useState<ScoreType>(ScoreType.NO_SCORE)

  const model = createExerciseScoreFormModel(scoreSetConfigurations, scoringOption)

  useEffect(() => {
    if (
      scoreSetConfigurations.findIndex((scoreSet) => scoreSet.scoreType === ScoreType.TEXT_FROM_ATHLETE) >= 0 ||
      (scoringOption !== null &&
        !scoringOption.startsWith("SET_N_FOR_SCOREBOARD_") &&
        !model.availableScoreResultTypes.some((scoreResultType) => scoreResultType.value === scoringOption))
    )
      setScoringOption(null)
  }, [scoringOption, model])

  useEffect(() => {
    if (scoreSetConfigurations.length > 0 && scoreSetConfigurations[0]) {
      if (scoringOption && scoringOption.startsWith("SET_N_FOR_SCOREBOARD_")) {
        const splitValue = scoringOption.split("_")
        const index = splitValue[splitValue.length - 1]

        if (index) {
          const referenceSet = scoreSetConfigurations[parseInt(index)]
          if (referenceSet) {
            setScoreType(referenceSet.scoreType)
          }
        }
      } else {
        setScoreType(scoreSetConfigurations[0].scoreType)
      }
    }
  }, [scoreSetConfigurations, scoringOption])

  const addScoreSetRow = () => {
    setScoreSetConfigurations([
      ...scoreSetConfigurations,
      { id: null, displayOrder: scoreSetConfigurations.length, scoreType: ScoreType.TIME },
    ])
  }

  const removeScoreSetRow = (index: number) => {
    const newScoreSetConfigurations = scoreSetConfigurations
      .filter((_, i) => i !== index)
      .map((scoreSet, i) => ({ ...scoreSet, displayOrder: i }))
    setScoreSetConfigurations(newScoreSetConfigurations)

    // if there's only one score set row, set the scoring option to null
    if (newScoreSetConfigurations.length === 1) {
      setScoringOption(null)
    }
  }

  const updateScoreSetRow = (index: number, scoreType: ScoreType) => {
    setScoreSetConfigurations(
      scoreSetConfigurations.map((scoreSet, i) => (i === index ? { ...scoreSet, scoreType } : scoreSet)),
    )
  }

  const updateScoringOption = (value: ScoreResultDraftType) => {
    setScoringOption(value)
  }

  return {
    addScoreSetRow,
    removeScoreSetRow,
    updateScoreSetRow,
    updateScoringOption,
    scoreSetConfigurations,
    scoringOption,
    scoreType,
  }
}
